/**
 * Vendor-SVG substrate loader for the RCP-3500 panel.
 *
 * The panel renders Sony's own vendor schematic inline and binds the
 * operator-curated component manifest onto the live DOM via data
 * attributes. CSS handles per-state visual treatment (illumination,
 * LED on/off, label callouts) by targeting those data attributes.
 *
 * The primitive-id assignment here mirrors the rcp-annotator
 * workbench's extractPrimitives algorithm byte-for-byte:
 *
 *   - skip defs / clipPath / mask / style subtrees
 *   - recurse into <g>
 *   - count only leaf primitives (path / rect / circle / ellipse /
 *     polygon / polyline / line / text)
 *   - skip primitives with no resolved fill AND no resolved stroke
 *     (the CSS-class-driven `class="st0"` clip references in the
 *     vendor file)
 *   - skip page-spanning primitives whose bbox covers ≥ 90 % of the
 *     SVG viewBox in both axes (CAD construction guides)
 *   - skip zero-area primitives
 *   - id = p<index4>-<tag>, zero-padded so the manifest's stable
 *     references resolve regardless of how many primitives precede
 *
 * Any divergence here breaks the id contract with components.json;
 * the workbench and this module must move together.
 */

import svgSource from '../assets/panel.svg?raw';
import componentsData from '../geometry/components.json' with { type: 'json' };
import inventoryData from '../inventory.json' with { type: 'json' };
import { isCompoundPath, splitCompoundPath } from './svg-path.ts';

export const PRIMITIVE_ID_ATTR = 'data-prim-id';
export const COMPONENT_ID_ATTR = 'data-component-id';
export const COMPONENT_TYPE_ATTR = 'data-component-type';
export const COMPONENT_ROLE_ATTR = 'data-component-role';
export const COMPONENT_LABEL_ATTR = 'data-component-label';
export const COMPONENT_BLOCK_ATTR = 'data-component-block';
export const COMPONENT_STATE_ATTR = 'data-component-state';
export const ILLUMINATION_COLOUR_VAR = '--component-illum';
export const ILLUMINATION_NAME_ATTR = 'data-illum-name';
/**
 * Marker on the &lt;text&gt; elements that bindSubstrate injects when a
 * component carries both a `labelBBox` (operator-curated silkscreen
 * geometry) and a non-empty `label` string. The CSS stylesheet uses
 * this attribute to pin SST as the font-family without affecting any
 * other text in the substrate. The attribute value is the component
 * id so the operator can correlate a specific glyph back to its
 * manifest entry via dev-tools.
 */
export const LABEL_OVERLAY_ATTR = 'data-label-overlay';
/**
 * Height-to-font-size ratio applied when deriving the SVG `font-size`
 * attribute from a labelBBox. Sony's silkscreen ink sits inside the
 * box with a small breathing margin top + bottom; 0.86 lands the SST
 * cap-height on the box and prevents descenders clipping the lower
 * edge for the few labels that carry them ('p', 'g', 'y').
 */
const LABEL_FONT_SIZE_RATIO = 0.86;
/**
 * Marker on every primitive describing whether the vendor original
 * carried ink in its fill, its stroke, or both. The substrate
 * stylesheet inverts only the channel that originally carried ink —
 * filled letterforms keep crisp edges instead of gaining a halo,
 * stroked outlines flip cleanly to silkscreen.
 */
export const INK_MODE_ATTR = 'data-prim-ink';

const PRIMITIVE_TAGS = new Set([
  'path',
  'rect',
  'circle',
  'ellipse',
  'polygon',
  'polyline',
  'line',
  'text',
]);

const SKIP_TAGS = new Set(['defs', 'clippath', 'mask', 'style']);

/**
 * Primitive ids that are NOT engraved on the real panel and must be hidden.
 * Hidden at bind time so they survive the deterministic manifest rebuild, which
 * regenerates `components.json` from scratch and does not carry operator tags
 * across.
 *
 * Scope corrected after operator review: most of the page-9 outline strokes are
 * NOT diagram artefacts. They are physical chassis features — raised plastic
 * ridges / bezels / button surrounds and the LCD frame — that read on the real
 * panel as subtle 3D relief, the same colour as the chassis. Those are KEPT and
 * rendered with the ridge bevel (see injectSubstrateStyles), not suppressed.
 *
 * What genuinely is not on the panel: the two full-width reference rules
 * (p0005, p0006) the diagram draws straight across the whole console as block
 * dividers. Only those stay here. Further true artefacts are added back as the
 * operator confirms them live via the picker (?candidates).
 */
export const IGNORED_PRIMITIVE_IDS: readonly string[] = [
  // Full-width console reference rules
  'p0005-path',
  'p0006-path',
  // Non-line junk panes the operator confirmed earlier — big duplicated
  // background rectangles the diagram draws behind sections, not on the panel.
  'p0651-path', 'p0652-path',
  'p0654-path', // p0653 (its twin) is now a module pane, see MODULE_BG_PRIMITIVE_IDS
  'p0655-path', 'p0656-path',
  'p0657-path', 'p0658-path',
  'p0659-path', 'p0660-path',
  'p0176-path',
  'p0515-path',
  'p0045-path', 'p0046-path',
];

/**
 * Primitives the label renderer must NOT hide. `renderDerivedLabels`
 * suppresses each vendor glyph outline it replaces with live type, but
 * its cluster matcher occasionally swallows a neighbouring silkscreen
 * mark that is not a letter. p0145/p0380 are the left half of the paint
 * WHITE underline bracket: they sit right next to the "WHITE" engraving,
 * got matched into that label cluster and were hidden, leaving the
 * bracket truncated. Force-showing them after the label pass restores
 * the bracket without loosening the matcher (which would risk other
 * labels). Operator-curated, so it survives the manifest rebuild.
 */
const KEPT_PRIMITIVE_IDS: readonly string[] = ['p0145-path'];

/**
 * Coloured silkscreen marks: a small printed bar beneath each function-coloured
 * button, inked in that button's colour (NOT illuminated — flat silkscreen). The
 * label-renderer's cluster matcher swallows them as if they were glyphs, hiding
 * them; we force them back and paint each in its button colour. Operator-mapped
 * (the bar belongs to the button directly above it).
 */
const SILK_COLOUR_MARKS: readonly { readonly id: string; readonly colour: string }[] = [
  { id: 'p0008-path', colour: '#e8920a' }, // PARA (amber)
  { id: 'p0009-path', colour: '#e8920a' }, // MASTER
  { id: 'p0010-path', colour: '#e8920a' }, // SUB ORDINATE
  { id: 'p0019-path', colour: '#2db84a' }, // CAM PW (green)
  { id: 'p0024-path', colour: '#10b898' }, // STANDARD (teal)
  { id: 'p0014-path', colour: '#cc2010' }, // START/BREAK (red)
  { id: 'p0016-path', colour: '#cc2010' }, // WHITE
  { id: 'p0017-path', colour: '#cc2010' }, // BLACK
  { id: 'p0154-path', colour: '#10b898' }, // IRIS/MB ACTIVE (teal)
  { id: 'p0169-path', colour: '#10b898' }, // PANEL ACTIVE (teal)
  { id: 'p0177-path', colour: '#bf1c10' }, // CALL (red)
];

/**
 * Primitives that are not outlines at all but module background PANES — areas
 * the panel fills with a discreet dark-grey silkprint (a touch lighter than the
 * steel-black chassis) to group a module, without any visible border. They must
 * therefore render as a flat dark-grey FILL with no stroke and no ridge bevel,
 * the opposite of the structural ridges. Operator-identified live.
 */
const MODULE_BG_PRIMITIVE_IDS: readonly string[] = [
  'p0096-path',
  'p0103-path',
  'p0108-path',
  'p0113-path',
  'p0653-path', // LCD-zone pane (twin p0654 stays hidden)
];

/**
 * Small silkscreen marks that must be SOLID off-white (filled), not just
 * outlined — little filled indicators the engrave rule would otherwise leave as
 * hollow strokes. Operator-identified.
 */
const SOLID_OFFWHITE_IDS: readonly string[] = [
  'p0124-path',
  'p0127-path',
  'p0130-path',
  'p0137-path',
  'p0140-path',
  'p0141-path',
];

/**
 * Small keys that keep their outline but take a matte dark grey-black fill —
 * recessed dark keytops, a touch above pure black. Stroke is left as-is so they
 * still read as outlined keys; only the fill is added. Operator-identified.
 */
const DARK_FILL_IDS: readonly string[] = [
  'p0122-path',
  'p0125-path',
  'p0128-path',
  'p0135-path',
  'p0138-path',
  'p0142-path',
];

/**
 * Physical chassis seams — NOT silkscreen. `p0004` is the panel face's outer
 * perimeter (where the steel ends against the background); `p0515` is the wall
 * ~3 vbu inside it, so the pair frames a shallow mating channel; `p0000` /
 * `p0002` are the top / bottom rack-ear bands butted against the face. The
 * engrave default paints them as flat white lines, which kills the
 * machined-metal read. We recolour them to a near-black seam and sink them with
 * the inset bevel so each reads as a recessed 3D groove (metal-to-metal /
 * metal-to-background), never a drawn line. Operator-confirmed via the dev picker.
 */
const SEAM_PRIMITIVE_IDS: readonly string[] = [
  'p0004-path',
  'p0515-path',
  'p0000-path',
  'p0002-path',
];

/**
 * Rack-mount screws — the 2 × ø5 holes in the ear flanges (`p0001` top, `p0003`
 * bottom). The vendor draws each as a thin off-white ring; we seat it as a black
 * Phillips screw in a matte rubber spacer. Operator-confirmed via the dev picker.
 */
const SCREW_PRIMITIVE_IDS: readonly string[] = ['p0001-path', 'p0003-path'];

/**
 * USB-cover detail (left edge, by the USB activity LED): a moulded rubber/plastic
 * flap and its hinge marks. Off-white is wrong — it is a dark rubber part, so it
 * takes a dark fill and a soft raised bevel for a restrained 3D read.
 * Operator-confirmed via the dev picker.
 */
const USB_DOOR_IDS: readonly string[] = ['p0118-path', 'p0119-path', 'p0120-path'];

/**
 * Dial cap-face squares — the flat top face inset into every rotary knob. The
 * vendor outlines each in off-white, which reads as a drawn white square on the
 * cap; on the real control the top is dark moulded plastic, so the edge takes a
 * dark-grey stroke instead. Restyled IN PLACE (no geometry touched). One square
 * per knob, across all 15 rotaries — paint (WHITE/BLACK R/G/B), ADJUST 1–4,
 * SELECT, DETAIL, ASSIGNABLE, COARSE and SENS. Operator-confirmed via the dev
 * picker (p0122 = WHITE R; the rest are its structural twins, centred on each
 * knob).
 */
const DIAL_FACE_PRIMITIVE_IDS: readonly string[] = [
  'p0122-path', // WHITE R
  'p0125-path', // WHITE G
  'p0128-path', // WHITE B
  'p0138-path', // BLACK R
  'p0135-path', // BLACK G
  'p0142-path', // BLACK B
  'p0133-path', // ASSIGNABLE
  'p0131-path', // DETAIL
  'p0055-path', // ADJUST 1
  'p0053-path', // ADJUST 2
  'p0051-path', // ADJUST 3
  'p0049-path', // ADJUST 4
  'p0047-path', // SELECT
  'p0152-path', // COARSE
  'p0149-path', // SENS
];

/**
 * Unbound LED indicator boxes (the D EXT / EXT / F DROP group) that get the same
 * unlit treatment as the bound LEDs — a frosted translucent off-white window
 * with no outline. The bound LEDs are handled by the led CSS rule.
 */
const FROSTED_LED_BOX_IDS: readonly string[] = [
  'p0179-path',
  'p0184-path',
  'p0185-path',
  'p0186-path',
];

/**
 * Tiny activity LEDs — same unlit frosted-glass look as the indicator windows
 * but a touch DARKER (lower-opacity milk), so the small dots read as dimmer
 * unpowered LEDs. No outline. Operator-identified.
 */
const SMALL_LED_IDS: readonly string[] = [
  // p0007-path removed — operator confirms it is chassis, not an LED.
  'p0065-path',
  'p0098-path',
  'p0099-path',
  'p0100-path',
  'p0105-path',
  'p0110-path',
];

/**
 * Colour class per bound indicator LED (component id → set). The class drives
 * the lit colour and, in the OFF/ON/TEST switch's TEST mode, the lamp-test
 * blink sequence (data-led-set):
 *   'amber' — single amber state
 *   'green' — single green state
 *   'gar'   — three states: green, amber, red
 * The unbound mini-LEDs (SELECT-LED, USB, ND, CC, MASTER GAIN) are NOT included:
 * they are not individually modelled, so they are left for proper annotation
 * rather than guessed.
 */
const LED_SET: Readonly<Record<string, string>> = {
  'cmp-iris-bargraph': 'green',
  'cmp-led-network': 'green',
  'cmp-led-scene-file': 'amber',
  'cmp-led-shutter-deg': 'amber',
  'cmp-led-shutter-sls': 'amber',
  'cmp-led-shutter-sht': 'amber',
  'cmp-led-shutter-ecs': 'amber',
  'cmp-led-alarm': 'gar',
  'cmp-led-cable': 'gar',
};

/**
 * Colour class per UNBOUND mini-LED (prim-id → set), filled in as the operator
 * identifies them through the dev picker. Same sets as LED_SET: select = amber;
 * usb / nd / cc / master-gain = gar (green/amber/red).
 */
const MINI_LED_SET: Readonly<Record<string, string>> = {
  'p0065-path': 'amber', // SELECT LED
  'p0098-path': 'gar', // ND LED
  'p0105-path': 'gar', // CC LED
  'p0110-path': 'gar', // MASTER GAIN LED
  'p0100-path': 'gar', // USB read/write LED
  'p0099-path': 'green', // ASSIGN-dial LED (inferred green from the ASSIGNABLE "when-assigned" state)
};

/**
 * Formerly the dark vendor-glyph LED legends. Every member is now rendered as
 * dark LIVE TYPE through `SILKSCREEN_LEGENDS` (the iris scale additionally clipped
 * so it never renders under the lever disc), so this list is intentionally empty
 * — kept only so the old bind-time loop and the `forceShow` spread stay no-ops.
 */
const DARK_LED_TEXT_IDS: readonly string[] = [];

/**
 * Silkscreen text the catalogue/header matchers in `renderDerivedLabels` never
 * convert to live type — control labels that clustered with a header, free
 * legends, the LED‑window legends and the iris f‑number scale. Each entry names
 * the exact vendor glyph prim-ids (the anchor), so the legend pass measures
 * their box, draws the string as live type there, and hides the glyphs — no
 * glyph-to-text guessing, and coverage is complete by construction (the sweep
 * probe is the regression gate).
 *
 * `text` = one label. `rows` = a vertical stack: the anchor glyphs are grouped
 * into baseline rows top→bottom and each row gets the matching string (used for
 * the multi-line LED legends and the iris scale). `dark: true` paints the legend
 * near-black so it reads on a frosted LED window and silhouettes when lit.
 * `hideOnly: true` suppresses a duplicate/shadow glyph layer without drawing.
 */
interface LegendEntry {
  readonly anchorIds: readonly string[];
  readonly text?: string;
  readonly rows?: readonly string[];
  readonly dark?: boolean;
  readonly hideOnly?: boolean;
  // Clip the drawn text to the panel MINUS the iris lever disc, so a live label
  // that overlaps the physically-proud lever never renders in the lever zone
  // (the live-type layer sits above the lever and would otherwise bleed through).
  readonly clipLever?: boolean;
}
const SILKSCREEN_LEGENDS: readonly LegendEntry[] = [
  // Function matrix — labels/headers the matcher dropped (they clustered together).
  { anchorIds: ['p0226-path', 'p0227-path', 'p0228-path', 'p0229-path', 'p0230-path'], text: 'CAM PW' },
  { anchorIds: ['p0209-path', 'p0210-path', 'p0211-path', 'p0212-path'], text: 'TEST' },
  {
    anchorIds: ['p0213-path', 'p0214-path', 'p0215-path', 'p0216-path', 'p0217-path', 'p0218-path', 'p0219-path', 'p0220-path', 'p0221-path'],
    text: 'AUTO SETUP',
  },
  // Single digits printed by their buttons (matcher leaves single chars as vendor).
  { anchorIds: ['p0349-path'], text: '1' }, // CUSTOM 1
  { anchorIds: ['p0348-path'], text: '2' }, // CUSTOM 2
  { anchorIds: ['p0396-path'], text: '1' }, // SCENE 1
  { anchorIds: ['p0397-path'], text: '2' },
  { anchorIds: ['p0398-path'], text: '3' },
  { anchorIds: ['p0399-path'], text: '4' },
  { anchorIds: ['p0400-path'], text: '5' },
  // Panel-status / iris lever labels.
  { anchorIds: ['p0417-path', 'p0418-path'], text: 'F4' },
  { anchorIds: ['p0471-path', 'p0472-path', 'p0473-path', 'p0474-path'], text: 'IRIS' },
  // LED-window legends — dark on the frosted windows.
  {
    anchorIds: ['p0429-path', 'p0430-path', 'p0431-path', 'p0432-path', 'p0433-path', 'p0434-path', 'p0435-path', 'p0436-path', 'p0437-path', 'p0438-path', 'p0439-path', 'p0440-path'],
    rows: ['D EXT', 'EXT', 'F DROP'],
    dark: true,
  },
  // Iris bargraph f-number scale — eight rows, top (open) to bottom (closed).
  // Clipped to the panel minus the lever disc so the rows the lever covers never
  // bleed through it (live text is layered above the lever).
  {
    anchorIds: ['p0419-path', 'p0420-path', 'p0421-path', 'p0422-path', 'p0423-path', 'p0424-path', 'p0425-path', 'p0426-path', 'p0427-path', 'p0428-path', 'p0441-path', 'p0442-path', 'p0443-path', 'p0516-path', 'p0517-path', 'p0518-path', 'p0519-path', 'p0520-path', 'p0521-path', 'p0632-path', 'p0633-path', 'p0634-path'],
    rows: ['2.0', '2.8', '4', '5.6', '8', '11', '16', 'CLS'],
    dark: true,
    clipLever: true,
  },
  // Duplicate/shadow layer of the D EXT / EXT / F DROP legends — hide, no draw.
  {
    anchorIds: ['p0274-path', 'p0275-path', 'p0276-path', 'p0277-path', 'p0278-path', 'p0279-path', 'p0280-path', 'p0281-path', 'p0282-path', 'p0283-path', 'p0284-path', 'p0285-path'],
    hideOnly: true,
  },
];

/**
 * The iris LEVER assembly — cap disc, master-black ring, the index-mark ring and
 * the centre pointer. Physically the lever stands ~5 cm proud of the panel face,
 * so it occludes the bargraph LED legends it travels over. The dark LED text is
 * re-appended last (above its frosted window), which would otherwise leave the
 * f-numbers bleeding through the disc — so the whole lever is lifted ABOVE that
 * text afterwards. Every id shares the bargraph's parent <g>, so re-appending
 * only reorders siblings and each primitive keeps its own placement transform.
 */
const LEVER_ASSEMBLY_IDS: readonly string[] = [
  'p0635-path', 'p0636-path', 'p0637-path', 'p0638-path', 'p0639-path', 'p0640-path',
  'p0641-path', 'p0642-path', 'p0643-path', 'p0644-path', 'p0645-path', 'p0646-path',
  'p0647-path', 'p0648-path', 'p0649-path', 'p0650-path',
];

/**
 * Unbound key caps that render as hollow off-white outlines but should read as
 * standard milky off-white keycaps when unlit — same look as the bound buttons.
 * p0111 is the MASTER GAIN up-key, which the label matcher had wrongly hidden as
 * a glyph (its siblings p0112/p0116/p0117 stayed visible); it is force-shown.
 */
const MILKY_KEY_IDS: readonly string[] = [
  'p0101-path', 'p0102-path', 'p0106-path', 'p0107-path',
  'p0111-path', 'p0112-path', 'p0116-path', 'p0117-path',
];

/** LCD primitives: p0044 is the oversized outer pane (bound but outside the
 * bezel) that the operator wants gone; p0057 is the real glass window, inside
 * the bezel, which becomes the dark screen and the touchscreen-overlay bounds. */
const LCD_OUTER_FRAME_ID = 'p0044-path';
const LCD_SCREEN_ID = 'p0057-path';

/**
 * Primitives that are physical raised relief — moulded chassis ridges / bezels
 * a millimetre proud of the steel face, same dark colour as the chassis, read
 * only by their 3D edge. Rendered with a chassis-grey stroke and the #rcp-ridge
 * bevel instead of the off-white engrave. This is a TARGETED list of the exact
 * ids the operator has confirmed — it never restyles silkscreen wholesale.
 * p0058 = the LCD outer bezel; p0171/p0172 = the PANEL ACTIVE button surround.
 */
const RIDGE_PRIMITIVE_IDS: readonly string[] = [
  // Special push-button surrounds — ONLY the exact ids the operator confirmed
  // by clicking. Inner rings that were not clicked stay default off-white;
  // nothing is generalised. (p0064 AUTO SETUP is a printed off-white line, not
  // a bevel — deliberately absent.)
  'p0041-path', 'p0042-path', 'p0043-path', // STANDARD
  'p0076-path', 'p0077-path', // STORE
  'p0079-path', 'p0080-path', // ABSOLUTE
  'p0082-path', 'p0083-path', // FLARE (same model as ABSOLUTE)
  'p0088-path', // WHITE
  'p0089-path', // BLACK
  'p0091-path', // CAM PW
  'p0092-path', // TEST
  'p0093-path', // BARS
  'p0094-path', // CLOSE
  'p0164-path', 'p0165-path', // IRIS/MB ACTIVE
  'p0167-path', 'p0168-path', // AUTO IRIS
  'p0171-path', 'p0172-path', // PANEL ACTIVE
  // Group-surround frames
  'p0087-path', // WHITE / BLACK
  'p0090-path', // CAM PW group
  // LCD bezel / recess (p0058 outer + the recess detail pieces)
  'p0058-path',
  'p0059-path', 'p0060-path', 'p0061-path', 'p0062-path',
  // Top-edge chassis detail — a small moulded square the vendor draws off-white;
  // it is a physical part, so it reads as a dark-grey embossed relief instead of
  // a white line. Operator-confirmed via the dev picker.
  'p0007-path',
];

/**
 * Ridge ids whose lower edge must be clipped away — the OUTER moulded lip is
 * three-sided (left / top / right) and runs out flush at the bottom toward the
 * label, so the closed-rectangle vendor stroke gets its bottom edge (and bevel
 * shadow) removed via #rcp-ridge-open-bottom. Exactly the ids the operator
 * flagged as "outer parts, no line at the bottom".
 */
const OPEN_BOTTOM_RIDGE_IDS: readonly string[] = [
  // Every button-surround ring (outer AND inner) and the group frames get the
  // open bottom, clipped by the same fraction, so each ring ends at the same
  // depth and the buttons read consistently. The LCD bezel (p0058) is a full
  // four-sided frame and is excluded.
  'p0041-path', 'p0042-path', 'p0043-path', // STANDARD
  'p0076-path', 'p0077-path', // STORE
  'p0079-path', 'p0080-path', // ABSOLUTE
  'p0082-path', 'p0083-path', // FLARE
  'p0088-path', // WHITE
  'p0089-path', // BLACK
  'p0091-path', // CAM PW
  'p0092-path', // TEST
  'p0093-path', // BARS
  'p0094-path', // CLOSE
  'p0164-path', 'p0165-path', // IRIS/MB ACTIVE
  'p0167-path', 'p0168-path', // AUTO IRIS
  'p0171-path', 'p0172-path', // PANEL ACTIVE
  'p0087-path', // WHITE / BLACK group
  'p0090-path', // CAM PW group
];

export interface SubstrateResult {
  readonly svg: SVGSVGElement;
  readonly primitiveCount: number;
  readonly boundComponentCount: number;
  readonly unresolvedPrimitiveIds: readonly string[];
}

/** Parse the vendor SVG into a fresh, detached `<svg>` element. */
export function parsePanelSvg(): SVGSVGElement {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgSource, 'image/svg+xml');
  const root = doc.documentElement;
  if (root.tagName.toLowerCase() !== 'svg') {
    throw new Error(`Vendor panel SVG root is <${root.tagName}>, expected <svg>`);
  }
  return root as unknown as SVGSVGElement;
}

/**
 * Walk a *mounted* SVG, assign stable primitive ids, then bind each
 * component from components.json onto the matching primitive nodes.
 * The SVG MUST be in the live DOM (or an offscreen-but-laid-out
 * container) so getBBox / getComputedStyle resolve.
 */
/**
 * Shatter every compound &lt;path&gt; in the SVG into a sequence of
 * single-sub-path siblings so each visually-distinct ink mark is an
 * independently addressable primitive. Mirror of the workbench's
 * explodeCompoundPaths so primitive IDs stay in lock-step.
 */
function explodeCompoundPaths(svg: SVGSVGElement): void {
  const ns = 'http://www.w3.org/2000/svg';
  const paths = Array.from(svg.querySelectorAll('path'));

  // Phase 1 — READ ONLY. Decide every split up front while the DOM is still
  // pristine. Interleaving getComputedStyle with the insert/remove writes below
  // forced a synchronous style+layout recalc on each iteration (measured ~161ms
  // of layout thrash on load); splitting one path never changes another path's
  // computed fill, so reading all fills first yields byte-identical decisions.
  const work: { path: Element; parent: Node; subs: string[] }[] = [];
  for (const path of paths) {
    const parent = path.parentNode;
    if (!parent) continue;
    const d = path.getAttribute('d');
    if (!d || !isCompoundPath(d)) continue;
    // Filled compound paths typically encode letterform / digit holes
    // (the inner ring of an O, P, R, 0, 6, 8, 9 etc.) as additional
    // sub-paths within the same <path> element — the SVG fill-rule
    // resolves them as cutouts at render time. Splitting them into N
    // siblings turns each hole sub-path into its own filled disc in
    // the same colour, destroying the cutout and collapsing the letter
    // into a solid blob. Stroke-only compound paths (Sony's CAD lines,
    // separators, outlines) carry no fill and each sub-stroke renders
    // independently — those are safe to split.
    const computedFill = window.getComputedStyle(path).fill;
    const hasFill =
      computedFill !== 'none' && computedFill !== '' && computedFill !== 'rgba(0, 0, 0, 0)';
    if (hasFill) continue;
    const subs = splitCompoundPath(d);
    if (subs.length <= 1) continue;
    work.push({ path, parent, subs });
  }

  // Phase 2 — WRITE ONLY. No layout reads here, so the DOM mutations batch into
  // a single recalc instead of one per path. Order is preserved, so primitive
  // ids stay in lock-step with the workbench extractor.
  for (const { path, parent, subs } of work) {
    for (const subD of subs) {
      const clone = svg.ownerDocument.createElementNS(ns, 'path');
      for (const attr of Array.from(path.attributes)) {
        if (attr.name === 'd') continue;
        clone.setAttribute(attr.name, attr.value);
      }
      clone.setAttribute('d', subD);
      parent.insertBefore(clone, path);
    }
    parent.removeChild(path);
  }
}

export function bindSubstrate(svg: SVGSVGElement): SubstrateResult {
  // Match the workbench's extraction order: explode compounds first
  // so primitive IDs match byte-for-byte across both pipelines.
  explodeCompoundPaths(svg);

  const vbBase = svg.viewBox.baseVal;
  const viewBox = {
    width: vbBase.width || 1,
    height: vbBase.height || 1,
  };

  let count = 0;
  const elById = new Map<string, SVGGraphicsElement>();

  const walk = (node: Element): void => {
    const tag = node.tagName.toLowerCase();
    if (SKIP_TAGS.has(tag)) return;

    if (tag === 'g') {
      for (const child of Array.from(node.children)) walk(child);
      return;
    }

    if (!PRIMITIVE_TAGS.has(tag)) {
      for (const child of Array.from(node.children)) walk(child);
      return;
    }

    const graphics = node as SVGGraphicsElement;
    const computed = window.getComputedStyle(graphics);
    const computedFill = computed.fill;
    const computedStroke = computed.stroke;
    const hasFill =
      computedFill !== 'none' && computedFill !== '' && computedFill !== 'rgba(0, 0, 0, 0)';
    const hasStroke =
      computedStroke !== 'none' && computedStroke !== '' && computedStroke !== 'rgba(0, 0, 0, 0)';
    if (!hasFill && !hasStroke) return;

    let bbox: { x: number; y: number; width: number; height: number };
    try {
      const b = graphics.getBBox();
      bbox = { x: b.x, y: b.y, width: b.width, height: b.height };
    } catch {
      return;
    }
    if (bbox.width >= viewBox.width * 0.9 && bbox.height >= viewBox.height * 0.9) return;
    if (bbox.width === 0 && bbox.height === 0) return;

    const id = `p${String(count).padStart(4, '0')}-${tag}`;
    count += 1;
    graphics.setAttribute(PRIMITIVE_ID_ATTR, id);
    // Tag whether this primitive's chassis ink lives in its fill or
    // its stroke. The injected substrate <style> uses this to invert
    // only the colour channel that was actually carrying ink in the
    // vendor source — without that, filled letterforms (Sony's
    // st10-class label paths) would gain a 1 px outline halo and
    // smudge against the dark chassis.
    if (hasFill && !hasStroke) graphics.setAttribute(INK_MODE_ATTR, 'fill');
    else if (!hasFill && hasStroke) graphics.setAttribute(INK_MODE_ATTR, 'stroke');
    else graphics.setAttribute(INK_MODE_ATTR, 'both');
    elById.set(id, graphics);
  };

  for (const child of Array.from(svg.children)) walk(child);

  // Operator-flagged "ignored" primitives — page-9 diagram artefacts that are
  // NOT on the real panel (full-width reference rules etc.). The operator tags
  // these in the workbench, but build-bindings regenerates components.json from
  // scratch and never carried the tags across, so they were wiped on every
  // rebuild and kept reappearing. Pinning them here keeps them hidden for good,
  // independent of the derived manifest. Add ids as they are confirmed against
  // the reference photograph; never include a primitive a control binds.
  for (const id of IGNORED_PRIMITIVE_IDS) {
    const el = elById.get(id);
    if (el) el.style.display = 'none';
  }


  // Physical raised ridges / bezels: chassis-grey stroke + subtle bevel, only
  // for the exact operator-confirmed ids. Inline !important beats the default
  // off-white engrave rule; everything not in this list stays untouched.
  for (const id of RIDGE_PRIMITIVE_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('stroke', 'var(--chassis-ridge, #34343a)', 'important');
    el.style.setProperty('fill', 'none', 'important');
    el.style.setProperty('filter', 'url(#rcp-ridge)', 'important');
  }

  // Solid off-white marks: add an off-white fill so closed indicators read as
  // solid. The stroke is left in place (the substrate engrave already paints it
  // off-white) — clearing it would erase any OPEN-path arrow that has no fill
  // area to show. Re-append each to its parent so the symbol draws ON TOP of its
  // (now dark-filled) keytop, which otherwise paints over it.
  for (const id of SOLID_OFFWHITE_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('fill', 'var(--chassis-engrave, #d8d4c8)', 'important');
    el.parentNode?.appendChild(el);
  }

  // Matte dark keytops: keep the outline, add a dark grey-black fill.
  for (const id of DARK_FILL_IDS) {
    const el = elById.get(id);
    if (el) el.style.setProperty('fill', 'var(--chassis-keytop, #1a1a1d)', 'important');
  }

  // Physical seams / outer edge → recessed dark groove, never a white line (see
  // SEAM_PRIMITIVE_IDS). The inset bevel sinks each below the surface, so the ear
  // joints read as metal-to-metal channels and the perimeter as the steel edge.
  // Stroke width is left to the engrave rule's 1px non-scaling default.
  for (const id of SEAM_PRIMITIVE_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('fill', 'none', 'important');
    el.style.setProperty('stroke', 'var(--chassis-seam, #0c0c0e)', 'important');
    el.style.setProperty('filter', 'url(#rcp-ridge-inset)', 'important');
  }

  // USB cover → dark moulded rubber with a soft raised bevel (see USB_DOOR_IDS).
  for (const id of USB_DOOR_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('fill', 'var(--chassis-rubber, #202024)', 'important');
    el.style.setProperty('stroke', 'var(--chassis-ridge, #34343a)', 'important');
    el.style.setProperty('filter', 'url(#rcp-ridge)', 'important');
  }

  // Dial cap-face squares → dark-grey edge, not the vendor's off-white outline
  // (see DIAL_FACE_PRIMITIVE_IDS). Only the stroke changes: the paint knobs keep
  // their dark keytop fill (square melts into a clean dark top face) and the
  // unfilled knobs keep fill:none (square reads as a subtle dark frame). The
  // existing fill is left untouched, so geometry and colour stay PDF-grounded.
  for (const id of DIAL_FACE_PRIMITIVE_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('stroke', 'var(--chassis-ridge, #34343a)', 'important');
  }

  // Rack screws → black Phillips heads in a matte rubber spacer (see
  // SCREW_PRIMITIVE_IDS). The head is restyled IN PLACE, so it keeps its
  // PDF-vector geometry and stays pickable; the spacer washer and the Phillips
  // cross are derived from the head's OWN local getBBox and inherit its placement
  // transform — never measured in screen / viewBox space — then inserted as
  // siblings behind and above it.
  {
    const NS = 'http://www.w3.org/2000/svg';
    for (const id of SCREW_PRIMITIVE_IDS) {
      const head = elById.get(id);
      if (!(head instanceof SVGGraphicsElement)) continue;
      const parent = head.parentNode;
      if (!parent) continue;
      let b: DOMRect;
      try {
        b = head.getBBox();
      } catch {
        continue;
      }
      if (b.width <= 0 || b.height <= 0) continue;
      const cx = b.x + b.width / 2;
      const cy = b.y + b.height / 2;
      const r = Math.min(b.width, b.height) / 2;
      const placement = head.getAttribute('transform');

      // Matte rubber spacer washer, a touch larger, seated behind the head.
      const washer = document.createElementNS(NS, 'circle');
      washer.setAttribute('cx', String(cx));
      washer.setAttribute('cy', String(cy));
      washer.setAttribute('r', String(r * 1.5));
      washer.setAttribute('fill', 'var(--chassis-screw-spacer, #060607)');
      if (placement) washer.setAttribute('transform', placement);
      washer.dataset['screwPart'] = `${id}-spacer`;
      parent.insertBefore(washer, head);

      // Dark steel dome head (unitless rim width → 1px non-scaling via the
      // engrave rule's vector-effect; 0.6 keeps the rim finer than the default).
      head.style.setProperty('fill', 'url(#rcp-screw-head)', 'important');
      head.style.setProperty('stroke', '#09090b', 'important');
      head.style.setProperty('stroke-width', '0.6', 'important');

      // Phillips cross laid on top, sized relative to the head so it scales with
      // whatever placement transform the head carries.
      const k = r * 0.74;
      const cross = document.createElementNS(NS, 'path');
      cross.setAttribute(
        'd',
        `M ${String(cx - k)} ${String(cy)} H ${String(cx + k)} M ${String(cx)} ${String(cy - k)} V ${String(cy + k)}`,
      );
      cross.setAttribute('fill', 'none');
      cross.setAttribute('stroke', '#050506');
      cross.setAttribute('stroke-width', String(r * 0.22));
      cross.setAttribute('stroke-linecap', 'round');
      if (placement) cross.setAttribute('transform', placement);
      cross.dataset['screwPart'] = `${id}-cross`;
      parent.insertBefore(cross, head.nextSibling);
    }
  }

  // LCD: the bound primitive p0044 is an oversized outer pane that sits OUTSIDE
  // the raised bezel; hide it. The real glass is p0057 (inside the bezel) — make
  // it the dark screen. The touchscreen content overlay is anchored to p0057 in
  // resolveOverlayRect, so screen, bezel and content all coincide.
  const lcdOuter = elById.get(LCD_OUTER_FRAME_ID);
  if (lcdOuter) lcdOuter.style.setProperty('display', 'none', 'important');
  const lcdScreen = elById.get(LCD_SCREEN_ID);
  if (lcdScreen) {
    lcdScreen.style.setProperty('fill', 'rgba(8, 10, 12, 0.95)', 'important');
    lcdScreen.style.setProperty('stroke', 'none', 'important');
  }

  // Unbound LED indicator boxes (D EXT / EXT / F DROP) and the tiny activity
  // LEDs (near SAVE / network / alarm / cable). These carry no bound component,
  // so they are tagged data-led-indicator ('box' / 'mini') and their appearance
  // is driven from the panel stylesheet — frosted glass at rest, dead-dark when
  // the panel is powered OFF, lit in TEST — exactly like every other emissive
  // element under the OFF/ON/TEST switch. (Was inline-styled, which the switch
  // could not reach because inline !important outranks the stylesheet.)
  for (const id of FROSTED_LED_BOX_IDS) {
    const el = elById.get(id);
    if (el) {
      el.setAttribute('data-led-indicator', 'box');
      el.setAttribute('data-led-set', 'amber'); // D EXT / EXT / F DROP
    }
  }
  for (const id of SMALL_LED_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.setAttribute('data-led-indicator', 'mini');
    const set = MINI_LED_SET[id];
    if (set) el.setAttribute('data-led-set', set);
  }

  // Step / arrow keys (ND · CC · MASTER GAIN · SHUTTER up/down). These carry no
  // inventory binding, but they are ordinary off-white caps — the same class as
  // every other off-white key, only a different (triangular) shape. Tag them as
  // buttons so the panel stylesheet gives them the full cap treatment (off-white
  // at rest, amber when lit), they become clickable, and they follow the
  // OFF/ON/TEST power mode like every other key. No inline paint here — the
  // stylesheet owns their appearance so the lit state can actually take effect
  // (an inline !important fill would block it).
  for (const id of MILKY_KEY_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('display', '', 'important');
    el.setAttribute(COMPONENT_TYPE_ATTR, 'button');
  }

  // Module panes drawn BEHIND the controls (above the chassis base, below every
  // component): a flat dark-grey fill, NO outline. Each is recreated fresh and
  // inserted just after the substrate <style> so it sits behind every primitive
  // group, and the original is hidden. Filling them in place left them painting
  // OVER the controls (p0653 covered the LCD, p0113 covered the shutter ON key)
  // and an outline was wrong — they must read exactly like the other background
  // panes: plain grey, no line.
  {
    const svgScreen = svg.getScreenCTM();
    const NS = 'http://www.w3.org/2000/svg';
    const insertBack = (el: SVGElement): void => {
      const styleEl = svg.querySelector('style[data-rcp-substrate]');
      if (styleEl?.nextSibling) svg.insertBefore(el, styleEl.nextSibling);
      else svg.insertBefore(el, svg.firstChild);
    };
    const measureBox = (el: SVGGraphicsElement) => {
      if (!svgScreen) return null;
      const wasHidden = el.style.display === 'none';
      if (wasHidden) el.style.setProperty('display', '', 'important');
      const b = el.getBBox();
      const sc = el.getScreenCTM();
      if (wasHidden) el.style.setProperty('display', 'none', 'important');
      if (!sc) return null;
      const m = svgScreen.inverse().multiply(sc);
      const xs: number[] = [];
      const ys: number[] = [];
      for (const [px, py] of [
        [b.x, b.y],
        [b.x + b.width, b.y],
        [b.x, b.y + b.height],
        [b.x + b.width, b.y + b.height],
      ] as const) {
        const p = new DOMPoint(px, py).matrixTransform(m);
        xs.push(p.x);
        ys.push(p.y);
      }
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    };
    const sampleContour = (id: string, n: number): [number, number][] | null => {
      const el = elById.get(id);
      if (!el || !svgScreen || !(el instanceof SVGGeometryElement)) return null;
      const wasHidden = el.style.display === 'none';
      if (wasHidden) el.style.setProperty('display', '', 'important');
      const sc = el.getScreenCTM();
      const len = el.getTotalLength();
      if (wasHidden) el.style.setProperty('display', 'none', 'important');
      if (!sc) return null;
      const m = svgScreen.inverse().multiply(sc);
      const pts: [number, number][] = [];
      for (let i = 0; i <= n; i++) {
        const p = el.getPointAtLength((len * i) / n);
        const t = new DOMPoint(p.x, p.y).matrixTransform(m);
        pts.push([Math.round(t.x * 100) / 100, Math.round(t.y * 100) / 100]);
      }
      return pts;
    };

    // Rectangular module panes → flat grey rect at the back, original hidden.
    for (const id of MODULE_BG_PRIMITIVE_IDS) {
      const el = elById.get(id);
      if (!el) continue;
      const box = measureBox(el);
      if (!box) continue;
      const rect = document.createElementNS(NS, 'rect');
      rect.setAttribute('x', String(box.x));
      rect.setAttribute('y', String(box.y));
      rect.setAttribute('width', String(box.w));
      rect.setAttribute('height', String(box.h));
      rect.setAttribute('fill', 'var(--chassis-module, #242429)');
      rect.dataset['modulePane'] = id;
      insertBack(rect);
      el.style.setProperty('display', 'none', 'important');
    }

    // Connected iris/master-black/panel-control pane. p0006 is the straight top
    // rule; p0176 is the STEPPED lower contour (its left side sits at the y502
    // step, not the y533 bottom). The closed fill follows p0006 left-to-right,
    // the right connector down to p0176's right end, p0176's actual contour
    // back to its left end, and the Z closes the left connector up to p0006 —
    // tracing the real form. Plain grey, no outline, behind the controls.
    const top = sampleContour('p0006-path', 24);
    const cont = sampleContour('p0176-path', 60);
    const start = top?.[0];
    if (top && cont && start && cont.length > 1) {
      const d = [
        `M ${String(start[0])} ${String(start[1])}`,
        ...top.slice(1).map(([x, y]) => `L ${String(x)} ${String(y)}`),
        ...cont
          .slice()
          .reverse()
          .map(([x, y]) => `L ${String(x)} ${String(y)}`),
        'Z',
      ].join(' ');
      const pane = document.createElementNS(NS, 'path');
      pane.setAttribute('d', d);
      pane.setAttribute('fill', 'var(--chassis-module, #242429)');
      pane.dataset['connectedPane'] = 'iris-block';
      insertBack(pane);
    }
  }

  // LED legend text: dark, no outline, lifted above the frosted window.
  for (const id of DARK_LED_TEXT_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('fill', '#16161a', 'important');
    el.style.setProperty('stroke', 'none', 'important');
    el.parentNode?.appendChild(el);
  }

  // Lift the whole lever assembly onto one wrapper <g> at the top of its group,
  // so (a) it paints above the bargraph legends it travels over and (b) the iris
  // drag can move the cap as ONE piece by transforming this group, instead of
  // sliding only the fill off the rings (which decoupled and let the scale bleed
  // through). Same parent → children keep their own placement transforms; the
  // wrapper itself carries no transform.
  {
    const leverEls: Element[] = [];
    for (const id of LEVER_ASSEMBLY_IDS) {
      const el = elById.get(id);
      if (el) leverEls.push(el);
    }
    const first = leverEls[0];
    if (first?.parentNode) {
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('data-iris-lever-group', '');
      first.parentNode.appendChild(g);
      for (const el of leverEls) g.appendChild(el);
    }
  }

  // Iris lever metal rings — a shallow steel bevel, not flat white lines. The
  // lever is a machined metal knob seen top-down: the OUTER ring (p0636) sits
  // deeper (recessed), the INNER master-black ring (p0647) rises toward the
  // viewer. Muted steel stroke + the inset / raised ridge filters give the
  // few-mm 3D read the operator asked for, with no white outline.
  const leverOuter = elById.get('p0636-path');
  if (leverOuter) {
    leverOuter.style.setProperty('fill', 'none', 'important');
    leverOuter.style.setProperty('stroke', 'var(--chassis-knob-rim, #6e6c68)', 'important');
    leverOuter.style.setProperty('filter', 'url(#rcp-ridge-inset)', 'important');
  }
  const leverInner = elById.get('p0647-path');
  if (leverInner) {
    leverInner.style.setProperty('fill', 'none', 'important');
    leverInner.style.setProperty('stroke', 'var(--chassis-knob-rim, #6e6c68)', 'important');
    leverInner.style.setProperty('filter', 'url(#rcp-ridge)', 'important');
  }

  // Connect ALL three lever-housing relief rings into continuous capsules. The
  // slot housing is three nested rings, each drawn as a top cap + a bottom cap.
  // The caps' open-end legs (the V segments) were too short for the top and
  // bottom to meet, so each ring read as two loose arcs. Rewrite each cap's `d`
  // to extend those legs toward the gap so the top and bottom join — outer
  // (p0207/p0208), middle (p0205/p0206) and inner (p0203/p0204) alike. The arc
  // curves are untouched; only the straight legs grow. (Vendor geometry
  // deliberately extended per request.)
  const HOUSING_RING_PATHS: Record<string, string> = {
    'p0203-path': 'M0 -20 V1.026 C0 4.471 3.049 7.274 6.796 7.274 10.543 7.274 13.593 4.471 13.593 1.026 V-20',
    'p0204-path': 'M0 20 V-1.025 C0-4.471 3.049-7.273 6.796-7.273 10.543-7.273 13.593-4.471 13.593-1.025 V20',
    'p0205-path': 'M0 -20 V1.815 C0 6.3 3.881 9.936 8.669 9.936 13.457 9.936 17.338 6.3 17.338 1.815 V-20',
    'p0206-path': 'M0 20 V-1.815 C0-6.3 3.881-9.936 8.669-9.936 13.457-9.936 17.338-6.3 17.338-1.815 V20',
    'p0207-path': 'M0 -20 V12.574 C0 14.636 1.686 16.321 3.746 16.321 H26.303 C28.364 16.321 30.05 14.636 30.05 12.574 V-20',
    'p0208-path': 'M0 20 V-12.639 C0-14.701 1.686-16.388 3.746-16.388 H26.303 C28.364-16.388 30.05-14.701 30.05-12.639 V20',
  };
  for (const [id, d] of Object.entries(HOUSING_RING_PATHS)) {
    const el = elById.get(id);
    if (el) el.setAttribute('d', d);
  }

  // Coloured silkscreen bars beneath the function-coloured buttons: flat fill in
  // the button's colour, no glow. NO stroke — the bar is a short ~1.33 vbu band,
  // so the substrate's 1px non-scaling stroke nearly doubled its height (and
  // widened it slightly), making every bar read too tall and a touch too wide.
  // Filling only, at the vendor geometry's true size. Force-shown again in
  // renderDerivedLabels.
  for (const { id, colour } of SILK_COLOUR_MARKS) {
    const el = elById.get(id);
    if (!el) continue;
    el.style.setProperty('fill', colour, 'important');
    el.style.setProperty('stroke', 'none', 'important');
    el.style.setProperty('filter', 'none', 'important');
  }

  // STORE has no vendor silkscreen bar, unlike every other commit/function key —
  // Sony's page-9 simply omits it from the scene-file row. Synthesise one to the
  // identical convention: same x and width as the STORE cap, the same gap below
  // it and the same height as a real bar (both measured live from the PARA
  // p0008 / p0011 pair, so the bar tracks the vendor geometry instead of relying
  // on magic numbers), filled the STORE commit-red. Drawn straight in the SVG
  // user space and appended last so it sits on the face like the others.
  {
    const storeBtn = elById.get('p0075-path');
    const refBar = elById.get('p0008-path');
    const refBtn = elById.get('p0011-path');
    const svgCtm = svg.getScreenCTM();
    if (storeBtn && refBar && refBtn && svgCtm) {
      // Work in screen space, then map back into the SVG user space through the
      // svg's OWN screen CTM — the only transform a <rect> appended straight to
      // <svg> actually obeys. (Per-element getCTM mixes in nested group matrices
      // the new rect does not inherit, which threw the first attempt off-place.)
      const sb = storeBtn.getBoundingClientRect();
      const rb = refBar.getBoundingClientRect();
      const rt = refBtn.getBoundingClientRect();
      const gap = rb.top - rt.bottom; // the silkscreen gap below every cap
      const sx = sb.left;
      const sy = sb.bottom + gap;
      const inv = svgCtm.inverse();
      const tl = new DOMPoint(sx, sy).matrixTransform(inv);
      const br = new DOMPoint(sx + sb.width, sy + rb.height).matrixTransform(inv);
      const NS = 'http://www.w3.org/2000/svg';
      const bar = document.createElementNS(NS, 'rect');
      bar.setAttribute('x', String(Math.min(tl.x, br.x)));
      bar.setAttribute('y', String(Math.min(tl.y, br.y)));
      bar.setAttribute('width', String(Math.abs(br.x - tl.x)));
      bar.setAttribute('height', String(Math.abs(br.y - tl.y)));
      bar.setAttribute('fill', '#c01818');
      bar.setAttribute('stroke', 'none');
      bar.dataset['storeSilkBar'] = '';
      svg.appendChild(bar);
    }
  }

  // Clip the bottom edge off the outer three-sided lips. Many vendor primitives
  // sit under a y-flipped transform (negative vertical scale), so objectBounding
  // Box y=0 is the VISUAL bottom for them — using one clip would cut the wrong
  // edge. Pick the clip per element from the sign of its screen-CTM vertical
  // scale so the BOTTOM is always the edge removed.
  for (const id of OPEN_BOTTOM_RIDGE_IDS) {
    const el = elById.get(id);
    if (!el) continue;
    const ctm = el.getScreenCTM();
    const flipped = ctm ? ctm.d < 0 : false;
    el.style.setProperty(
      'clip-path',
      flipped ? 'url(#rcp-ridge-open-bottom-flip)' : 'url(#rcp-ridge-open-bottom)',
      'important',
    );
  }

  // Build a normalised label index from inventory so we can carry
  // each component's illumination metadata across.
  const inventoryByLabel = new Map<string, (typeof inventoryData.controls)[number]>();
  for (const ctl of inventoryData.controls) {
    const labels = [ctl.label, (ctl as { id?: string }).id ?? '']
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
      .map(normaliseLabel);
    for (const key of labels) {
      if (!inventoryByLabel.has(key)) inventoryByLabel.set(key, ctl);
    }
  }

  // Bind components onto primitives. Components also queue a label
  // overlay if they carry both a non-empty `label` and a labelBBox —
  // those are emitted as live <text> after the binding pass so they
  // sit at the top of the SVG's child order and paint on top of any
  // vendor-outlined letterforms underneath.
  let bound = 0;
  const unresolved: string[] = [];
  const overlays: LabelOverlay[] = [];
  for (const component of componentsData.components) {
    const inventoryMatch = lookupInventory(component, inventoryByLabel);
    const illumColour = inventoryMatch?.illumination?.colours[0]?.hex;
    const illumName = inventoryMatch?.illumination?.colours[0]?.name;
    const defaultState = inventoryMatch?.illumination?.defaultState;

    for (const pid of component.sourcePrimitiveIds) {
      const el = elById.get(pid);
      if (!el) {
        unresolved.push(`${component.id}→${pid}`);
        continue;
      }
      el.setAttribute(COMPONENT_ID_ATTR, component.id);
      el.setAttribute(COMPONENT_TYPE_ATTR, component.type);
      const ledSet = LED_SET[component.id];
      if (ledSet) el.setAttribute('data-led-set', ledSet);
      if (component.role) el.setAttribute(COMPONENT_ROLE_ATTR, component.role);
      if (component.label) el.setAttribute(COMPONENT_LABEL_ATTR, component.label);
      if (component.block) el.setAttribute(COMPONENT_BLOCK_ATTR, component.block);
      if (defaultState) el.setAttribute(COMPONENT_STATE_ATTR, defaultState);
      if (illumColour) {
        el.style.setProperty(ILLUMINATION_COLOUR_VAR, illumColour);
      }
      if (illumName) el.setAttribute(ILLUMINATION_NAME_ATTR, illumName);
      bound += 1;
    }

    // Read labelBBox via a structural cast because the committed
    // components.json doesn't carry a labelBBox on every entry yet;
    // the JSON-derived type therefore lacks the field. The workbench
    // is the only writer (see ComponentRecord) so the shape is stable
    // wherever it does appear.
    const withLabel = component as ComponentRecordLike;
    if (withLabel.labelBBox && withLabel.label.length > 0) {
      overlays.push({
        componentId: withLabel.id,
        text: withLabel.label,
        bbox: withLabel.labelBBox,
      });
    }
  }

  injectLabelOverlays(svg, overlays);

  return {
    svg,
    primitiveCount: count,
    boundComponentCount: bound,
    unresolvedPrimitiveIds: unresolved,
  };
}

/**
 * Subset of the workbench's ComponentRecord that the runtime cares
 * about. Declared here rather than imported because the workbench's
 * type lives in a sibling package and pulling its full graph into the
 * panel bundle would force a runtime dependency on React. Keep this
 * shape in sync with `tools/rcp-annotator/src/lib/componentManifest.ts`.
 */
interface ComponentRecordLike {
  readonly id: string;
  readonly type: string;
  readonly role: string;
  readonly label: string;
  readonly block: string;
  readonly sourcePrimitiveIds: readonly string[];
  readonly labelBBox?: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
}

interface LabelOverlay {
  readonly componentId: string;
  readonly text: string;
  readonly bbox: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
}

/**
 * Emit one &lt;text&gt; element per pending labelBBox. The text node is
 * appended at the very end of the SVG's child list so it paints above
 * every component primitive — the operator-curated label sits on top
 * of any vendor-outlined glyph underneath, ready for the day we strip
 * those outlines entirely once every label has a bbox.
 *
 * Positioning anchors the glyph centre on the bbox centre via
 * `text-anchor=middle` + `dominant-baseline=central`. font-size is
 * derived from the bbox height with a constant ratio so SST's cap
 * height lands precisely on the operator-drawn rectangle.
 */
function injectLabelOverlays(svg: SVGSVGElement, overlays: readonly LabelOverlay[]): void {
  if (overlays.length === 0) return;
  const ns = 'http://www.w3.org/2000/svg';
  for (const overlay of overlays) {
    const t = svg.ownerDocument.createElementNS(ns, 'text');
    const cx = overlay.bbox.x + overlay.bbox.width / 2;
    const cy = overlay.bbox.y + overlay.bbox.height / 2;
    t.setAttribute('x', String(cx));
    t.setAttribute('y', String(cy));
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('dominant-baseline', 'central');
    t.setAttribute('font-size', String(overlay.bbox.height * LABEL_FONT_SIZE_RATIO));
    t.setAttribute(LABEL_OVERLAY_ATTR, overlay.componentId);
    t.textContent = overlay.text;
    svg.appendChild(t);
  }
}

/**
 * Reduce a label string ("AUTO\nIRIS", "iris/mb active", "BLACK\nGAMMA")
 * to a stable key for cross-referencing components.json → inventory.
 * Lowercase, strip non-alphanumerics. The match is intentionally
 * forgiving so the operator doesn't have to mirror Sony's
 * silkscreen formatting exactly when annotating.
 */
function normaliseLabel(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

type InventoryControl = (typeof inventoryData.controls)[number];

/**
 * Best-effort lookup from a manifest component into the inventory's
 * rich metadata. Tries the label first (operator-authored), then
 * the role (often equal to the inventory id). Returns null if no
 * confident match — the component still renders, just without an
 * illumination palette overlay.
 */
function lookupInventory(
  component: (typeof componentsData.components)[number],
  index: ReadonlyMap<string, InventoryControl>,
): InventoryControl | null {
  const candidates = [component.label, component.role].filter(
    (s): s is string => typeof s === 'string' && s.length > 0,
  );
  for (const c of candidates) {
    const hit = index.get(normaliseLabel(c));
    if (hit) return hit;
  }
  return null;
}

/**
 * Inject a per-substrate <style> element into the SVG so the
 * vendor's 0.25 vbu strokes render at a sensible pixel width
 * regardless of panel size. The original geometry is untouched;
 * only the visual ink weight is normalised.
 */
/**
 * Inject the gradient / filter definitions the panel's CSS rules
 * reference (e.g. the iris cap gloss). These live in a dedicated
 * &lt;defs&gt; node prepended to the SVG so they resolve from any
 * `url(#…)` reference further down the tree.
 */
export function injectSubstrateDefs(svg: SVGSVGElement): void {
  const ns = 'http://www.w3.org/2000/svg';
  const defs = document.createElementNS(ns, 'defs');
  defs.setAttribute('data-rcp-substrate-defs', '');

  // 3D cap relief. A soft specular highlight from the upper-left lit by
  // a distant light gives every illuminated keycap a domed, raised
  // reading regardless of its fill colour — turning the flat vendor
  // outline into a translucent cap that catches studio light the way
  // the real polycarbonate buttons do. Distant (not point) light keeps
  // it independent of each cap's position/size. Built as markup because
  // the feSpecularLighting filter chain is far clearer inline than via
  // imperative createElementNS calls.
  defs.insertAdjacentHTML(
    'beforeend',
    `<filter id="rcp-cap-3d" color-interpolation-filters="sRGB" x="-12%" y="-12%" width="124%" height="124%">
       <feGaussianBlur in="SourceAlpha" stdDeviation="0.45" result="blur"/>
       <feSpecularLighting in="blur" surfaceScale="1.3" specularConstant="0.55"
         specularExponent="28" lighting-color="#ffffff" result="spec">
         <feDistantLight azimuth="235" elevation="64"/>
       </feSpecularLighting>
       <feComposite in="spec" in2="SourceAlpha" operator="in" result="specClip"/>
       <feMerge>
         <feMergeNode in="SourceGraphic"/>
         <feMergeNode in="specClip"/>
       </feMerge>
     </filter>`,
  );

  // Raised chassis ridge. Most page-9 outline strokes are not silkscreen — they
  // are physical plastic ridges / bezels / button surrounds moulded ~1 mm proud
  // of the steel face. They are the SAME dark colour as the chassis; the eye
  // reads them only by the relief. So this filter casts a soft dark shadow to
  // the lower-right and a faint light highlight to the upper-left of the stroke,
  // turning a flat line into a subtle bevelled edge. Deliberately understated —
  // a modern moulded ridge, never a heavy bezel. Paired with a chassis-grey
  // stroke in injectSubstrateStyles.
  defs.insertAdjacentHTML(
    'beforeend',
    `<filter id="rcp-ridge" color-interpolation-filters="sRGB" x="-60%" y="-60%" width="220%" height="220%">
       <feOffset in="SourceAlpha" dx="0.45" dy="0.6" result="dn"/>
       <feFlood flood-color="#000000" flood-opacity="0.55" result="dc"/>
       <feComposite in="dc" in2="dn" operator="in" result="shadow"/>
       <feOffset in="SourceAlpha" dx="-0.35" dy="-0.5" result="up"/>
       <feFlood flood-color="#ffffff" flood-opacity="0.16" result="uc"/>
       <feComposite in="uc" in2="up" operator="in" result="highlight"/>
       <feMerge>
         <feMergeNode in="shadow"/>
         <feMergeNode in="highlight"/>
         <feMergeNode in="SourceGraphic"/>
       </feMerge>
     </filter>`,
  );

  // Inset (recessed) ridge — the inverse of #rcp-ridge. Shadow to the UPPER-left,
  // highlight to the LOWER-right, so the stroke reads as sunk BELOW the surface
  // rather than raised above it. Used for the iris lever's outer steel ring,
  // which sits deeper than the inner ring that rises toward the viewer.
  defs.insertAdjacentHTML(
    'beforeend',
    `<filter id="rcp-ridge-inset" color-interpolation-filters="sRGB" x="-60%" y="-60%" width="220%" height="220%">
       <feOffset in="SourceAlpha" dx="-0.45" dy="-0.6" result="dn"/>
       <feFlood flood-color="#000000" flood-opacity="0.5" result="dc"/>
       <feComposite in="dc" in2="dn" operator="in" result="shadow"/>
       <feOffset in="SourceAlpha" dx="0.35" dy="0.5" result="up"/>
       <feFlood flood-color="#ffffff" flood-opacity="0.14" result="uc"/>
       <feComposite in="uc" in2="up" operator="in" result="highlight"/>
       <feMerge>
         <feMergeNode in="shadow"/>
         <feMergeNode in="highlight"/>
         <feMergeNode in="SourceGraphic"/>
       </feMerge>
     </filter>`,
  );

  // Open-bottom clip. The OUTER raised lip around a special button is moulded on
  // three sides only — left, top, right — and runs out flush to the chassis at
  // the bottom, toward the label. The vendor draws it as a closed rectangle, so
  // we clip the lower edge away (and the bevel shadow with it) while keeping the
  // three sides and their relief. objectBoundingBox units make this one clip
  // reusable across every lip regardless of size or placement transform; the
  // x/y overflow past 0..1 preserves the bevel highlight/shadow on the kept
  // sides.
  defs.insertAdjacentHTML(
    'beforeend',
    `<clipPath id="rcp-ridge-open-bottom" clipPathUnits="objectBoundingBox">
       <rect x="-0.6" y="-0.6" width="2.2" height="1.54"/>
     </clipPath>
     <clipPath id="rcp-ridge-open-bottom-flip" clipPathUnits="objectBoundingBox">
       <rect x="-0.6" y="0.06" width="2.2" height="1.54"/>
     </clipPath>`,
  );

  // Black Phillips screw head — a dark steel dome lit from the upper-left, so the
  // seated rack screws read as machined metal, not flat off-white rings.
  defs.insertAdjacentHTML(
    'beforeend',
    `<radialGradient id="rcp-screw-head" cx="38%" cy="33%" r="70%">
       <stop offset="0%" stop-color="#4a4a50"/>
       <stop offset="48%" stop-color="#26262b"/>
       <stop offset="100%" stop-color="#0a0a0c"/>
     </radialGradient>`,
  );

  svg.insertBefore(defs, svg.firstChild);
}

export function injectSubstrateStyles(svg: SVGSVGElement): void {
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.setAttribute('data-rcp-substrate', '');
  // The vendor SVG is line-art-on-white-paper. We render it inside a
  // dark Sony chassis, so the silkscreen / engraving has to invert
  // from #231916 ink to off-white. The rules below invert ONLY the
  // channel that originally carried ink:
  //
  //   - stroke-mode primitives (lines, outlines, st1–st4): flip the
  //     stroke colour and pin the width so 0.25 vbu strokes don't
  //     collapse below a pixel.
  //   - fill-mode primitives (filled letterforms, st10): flip the
  //     fill colour. No stroke gets added — a halo around dark
  //     letters would smudge them.
  //   - both: flip both.
  //
  // Specific physical-relief primitives (ridges, bezels, module panes)
  // are overridden individually in bindSubstrate; this default leaves
  // every other silkscreen line exactly as off-white engraving.
  style.textContent = `
    [${PRIMITIVE_ID_ATTR}] {
      vector-effect: non-scaling-stroke !important;
    }
    [${INK_MODE_ATTR}~='stroke'],
    [${INK_MODE_ATTR}='both'] {
      stroke: var(--chassis-engrave, #d8d4c8) !important;
      stroke-width: 1px !important;
    }
    [${INK_MODE_ATTR}='fill'],
    [${INK_MODE_ATTR}='both'] {
      fill: var(--chassis-engrave, #d8d4c8) !important;
    }
  `;
  svg.insertBefore(style, svg.firstChild);
}

/**
 * Replace Sony's vendor-outlined silkscreen control labels with live type in
 * the canonical UI face.
 *
 * Each silkscreen letter in the vendor vector is its own &lt;path&gt;, placed by a
 * per-glyph transform, so we measure each in the SVG's user (viewBox) space
 * via `svg.getScreenCTM().inverse() × el.getScreenCTM()` — `getBBox` alone is
 * glyph-local. Glyphs are segmented into words (baseline rows split by
 * horizontal gaps); each control then claims the nearby word-subset whose
 * letter count matches its inventory label exactly — the letter count is a
 * strong disambiguator in the dense function matrix where pure proximity
 * bleeds between neighbours. Matched outline glyphs are hidden and the label is
 * redrawn as a live &lt;text&gt; node (the `data-label-overlay` rules paint it in
 * the engrave colour and the UI face), so the panel reads as engraved type
 * rather than traced vector.
 *
 * Runtime-derived so it can be reviewed live; once confirmed the geometry is
 * baked into components.json for byte-deterministic rendering.
 */
export function renderDerivedLabels(svg: SVGSVGElement): void {
  const svgScreen = svg.getScreenCTM();
  if (!svgScreen) return;
  const inv = svgScreen.inverse();

  const userBox = (el: SVGGraphicsElement): { x: number; y: number; w: number; h: number } => {
    const b = el.getBBox();
    const cm = el.getScreenCTM();
    const m = cm ? inv.multiply(cm) : inv;
    const xs: number[] = [];
    const ys: number[] = [];
    for (const [px, py] of [
      [b.x, b.y],
      [b.x + b.width, b.y],
      [b.x, b.y + b.height],
      [b.x + b.width, b.y + b.height],
    ] as const) {
      const p = new DOMPoint(px, py).matrixTransform(m);
      xs.push(p.x);
      ys.push(p.y);
    }
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
  };

  const median = (a: readonly number[]): number => {
    const s = [...a].sort((m, n) => m - n);
    return s[Math.floor(s.length / 2)] ?? 0;
  };

  interface Glyph {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
    readonly cx: number;
    readonly cy: number;
    readonly el: SVGGraphicsElement;
  }

  // Bounding boxes of every bound control. Control GRAPHICS — a knob's pointer
  // triangle, an arrow-key's chevron — are filled marks too, but they sit ON a
  // control, never below it where a silkscreen label lives. Excluding glyphs
  // that fall on a control keeps them out of the label set, so they are never
  // hidden or replaced — earlier passes were deleting knob pointers and arrows.
  const controlBoxes: { x: number; y: number; w: number; h: number }[] = [];
  {
    const ids = new Set<string>();
    for (const el of Array.from(svg.querySelectorAll<SVGGraphicsElement>(`[${COMPONENT_ID_ATTR}]`))) {
      const id = el.getAttribute(COMPONENT_ID_ATTR);
      if (!id || ids.has(id)) continue;
      ids.add(id);
      const bs = Array.from(
        svg.querySelectorAll<SVGGraphicsElement>(`[${COMPONENT_ID_ATTR}="${id}"]`),
      ).map(userBox);
      const x = Math.min(...bs.map((b) => b.x));
      const y = Math.min(...bs.map((b) => b.y));
      controlBoxes.push({
        x,
        y,
        w: Math.max(...bs.map((b) => b.x + b.w)) - x,
        h: Math.max(...bs.map((b) => b.y + b.h)) - y,
      });
    }
  }
  const onControl = (px: number, py: number): boolean =>
    controlBoxes.some((b) => px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h);

  // 1. Visible silkscreen glyphs: unbound, letter-sized ink marks that are NOT
  //    sitting on a control (those are pointers / chevrons, not labels).
  const glyphs: Glyph[] = [];
  for (const node of Array.from(svg.querySelectorAll<SVGGraphicsElement>(`path[${INK_MODE_ATTR}]`))) {
    if (node.hasAttribute(COMPONENT_ID_ATTR)) continue;
    const b = userBox(node);
    if (b.w <= 0 || b.h <= 0 || b.w > 22 || b.h > 9) continue;
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    if (onControl(cx, cy)) continue;
    glyphs.push({ ...b, cx, cy, el: node });
  }

  // 2. Flood-cluster glyphs into label blocks. The letters of one label, and
  //    the one or two stacked lines of a multi-line label, sit within a small
  //    gap of each other; separate labels are farther apart. Union-find over
  //    that proximity yields one block per silkscreen label with its exact box.
  // Anisotropic: a narrow horizontal gap keeps neighbouring labels apart in the
  // dense matrix, while a wider vertical gap reliably merges the two stacked
  // lines of a multi-line label (e.g. PAINT / CUSTOM) into ONE block — so a
  // control claims and hides both lines together instead of leaving one line's
  // outline behind under cramped replacement type.
  const GAP_X = 1.4;
  const GAP_Y = 3.5;
  const adjacent = (a: Glyph, b: Glyph): boolean =>
    Math.max(b.x - (a.x + a.w), a.x - (b.x + b.w), 0) <= GAP_X &&
    Math.max(b.y - (a.y + a.h), a.y - (b.y + b.h), 0) <= GAP_Y;
  const parent = glyphs.map((_, i) => i);
  const find = (i: number): number => {
    let r = i;
    for (;;) {
      const p = parent[r];
      if (p === undefined || p === r) return r;
      r = p;
    }
  };
  for (let i = 0; i < glyphs.length; i += 1) {
    const gi = glyphs[i];
    if (!gi) continue;
    for (let j = i + 1; j < glyphs.length; j += 1) {
      const gj = glyphs[j];
      if (gj && adjacent(gi, gj)) parent[find(i)] = find(j);
    }
  }
  const grouped = new Map<number, Glyph[]>();
  glyphs.forEach((g, i) => {
    const root = find(i);
    const arr = grouped.get(root) ?? [];
    arr.push(g);
    grouped.set(root, arr);
  });

  interface Box {
    x: number;
    y: number;
    w: number;
    h: number;
  }
  interface Block extends Box {
    readonly gh: number;
    readonly els: readonly SVGGraphicsElement[];
    readonly glyphs: readonly Glyph[];
  }
  const blocks: Block[] = [...grouped.values()].map((gs) => {
    const x = Math.min(...gs.map((g) => g.x));
    const y = Math.min(...gs.map((g) => g.y));
    const x2 = Math.max(...gs.map((g) => g.x + g.w));
    const y2 = Math.max(...gs.map((g) => g.y + g.h));
    return { x, y, w: x2 - x, h: y2 - y, gh: median(gs.map((g) => g.h)), els: gs.map((g) => g.el), glyphs: gs };
  });

  // One uniform type size for every label, derived from Sony's own character
  // width (block width ÷ glyph count). Because Sony allots footprint width in
  // proportion to a label's length, a single size lets a label of any length
  // fit its footprint naturally — no per-label scaling and no horizontal
  // distortion (the earlier textLength fit stretched glyphs unevenly).
  const charWidths = blocks
    .filter((b) => b.glyphs.length >= 2)
    .map((b) => b.w / b.glyphs.length)
    .sort((a, b) => a - b);
  // A low percentile, not the median: size the type so even the densest labels
  // (CHARACTER, SATURATION) fit their footprint without the spacing fit having
  // to crush the letters together. Looser labels simply gain a little air.
  const charWidth = charWidths.length
    ? (charWidths[Math.floor(charWidths.length * 0.25)] ?? charWidths[0] ?? 2)
    : median(glyphs.map((g) => g.h)) * 0.55;
  const FONT_SIZE = charWidth / 0.5;

  // 3. Controls carrying a label, with their bounding box in user space.
  const edgeDist = (a: Box, b: Box): number =>
    Math.hypot(
      Math.max(b.x - (a.x + a.w), a.x - (b.x + b.w), 0),
      Math.max(b.y - (a.y + a.h), a.y - (b.y + b.h), 0),
    );
  interface Ctrl {
    readonly label: string;
    readonly box: Box;
    readonly own: Block[];
  }
  const seen = new Set<string>();
  const controls: Ctrl[] = [];
  for (const el of Array.from(svg.querySelectorAll<SVGGraphicsElement>(`[${COMPONENT_ID_ATTR}]`))) {
    const id = el.getAttribute(COMPONENT_ID_ATTR);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const label = el.getAttribute(COMPONENT_LABEL_ATTR) ?? '';
    if (!label) continue;
    const boxes = Array.from(
      svg.querySelectorAll<SVGGraphicsElement>(`[${COMPONENT_ID_ATTR}="${id}"]`),
    ).map(userBox);
    const x = Math.min(...boxes.map((b) => b.x));
    const y = Math.min(...boxes.map((b) => b.y));
    const box: Box = {
      x,
      y,
      w: Math.max(...boxes.map((b) => b.x + b.w)) - x,
      h: Math.max(...boxes.map((b) => b.y + b.h)) - y,
    };
    controls.push({ label, box, own: [] });
  }

  // 4. Assign each label block to its single nearest control (within range).
  //    A block of silkscreen ink belongs to whichever control it labels;
  //    headers and free-standing legends have no control within range and keep
  //    the original vendor outline. Block-centric assignment cannot split one
  //    label across two controls — the cause of the earlier doubling.
  const RANGE = 6;
  // Sony centres each control's silkscreen label directly beneath the control,
  // so score candidates by horizontal-centre alignment plus vertical gap
  // (penalising blocks that sit above), not by raw edge distance. In the
  // horizontally dense function matrix this picks the label under THIS cap
  // rather than a neighbour's that happens to be marginally closer.
  const centreX = (b: Box): number => b.x + b.w / 2;
  const labelScore = (c: Ctrl, b: Block): number => {
    const dx = Math.abs(centreX(b) - centreX(c.box));
    const below = b.y - (c.box.y + c.box.h);
    const vert = below >= 0 ? below : -below * 3 + 4;
    return dx * 1.8 + vert;
  };
  // Strict one-to-one: build every plausible control↔block pair, then claim
  // greedily best-score-first so each block backs exactly one control and no
  // control's union stretches across a neighbour's label (the overlap cause).
  const pairs: { c: Ctrl; b: Block; d: number }[] = [];
  for (const c of controls) {
    // Single-character labels (scene / custom digits, the paint R/G/B) are
    // printed ON their control, so they were excluded as control graphics and
    // have no block of their own. Leave them as Sony's own correctly-placed
    // vector rather than let the control claim a neighbour's glyph — that is how
    // scene "2"/"5" had been hijacking and hiding the ND/CC up-arrows.
    if (c.label.replace(/[^A-Za-z0-9]/gu, '').length <= 1) continue;
    for (const b of blocks) {
      if (edgeDist(b, c.box) > RANGE) continue;
      // A control's label sits tight against it — just below or overlapping,
      // never a full row away. Capping the downward gap stops a control from
      // claiming whatever sits in the next row down: scene "2"/"5" had been
      // grabbing the ND/CC up-arrows (a row below the scene buttons) and
      // hiding them, because the scene digits printed on the buttons were
      // excluded as control graphics, leaving the arrow as the nearest block.
      if (b.y - (c.box.y + c.box.h) > 5) continue;
      const d = labelScore(c, b);
      if (d <= 12) pairs.push({ c, b, d });
    }
  }
  pairs.sort((a, b) => a.d - b.d);
  const takenB = new Set<Block>();
  const takenC = new Set<Ctrl>();
  for (const p of pairs) {
    if (takenC.has(p.c) || takenB.has(p.b)) continue;
    takenC.add(p.c);
    takenB.add(p.b);
    p.c.own.push(p.b);
  }
  // A two-line label may survive as two blocks; let a control absorb an
  // unclaimed block stacked directly above/below its own with x-overlap.
  for (const c of controls) {
    if (c.own.length !== 1) continue;
    const own = c.own[0];
    if (!own) continue;
    for (const b of blocks) {
      if (takenB.has(b)) continue;
      const xOverlap = Math.min(own.x + own.w, b.x + b.w) - Math.max(own.x, b.x);
      const yGap = Math.max(b.y - (own.y + own.h), own.y - (b.y + b.h), 0);
      if (xOverlap > 0 && yGap <= 2) {
        c.own.push(b);
        takenB.add(b);
      }
    }
  }

  // 5. Hide each control's outline glyphs and redraw its KNOWN label text on the
  //    block geometry — exact position and size from the vendor ink, exact text
  //    from the catalogue. No glyph-to-text guessing.
  const ns = 'http://www.w3.org/2000/svg';
  // Draw one line of live type. `textLen` fits the glyphs to exactly the
  // original silkscreen width via lengthAdjust, so a label occupies the same
  // footprint as the vendor ink and can never spill into its neighbour — which
  // is what made wide names (CHARACTER, SATURATION) collide.
  const draw = (text: string, cx: number, cy: number, footprint: number, dark = false, clip = ''): void => {
    // Uniform size where it fits; shrink (never below a floor) only the few
    // labels denser than their footprint so CHARACTER / SATURATION fit without
    // the letters crushing together — and without distorting the others. No
    // textLength: lengthAdjust either scaled the glyphs (uneven sizes) or, on
    // 'spacing', crushed the dense labels into an unreadable overlap.
    const chars = text.replace(/[^A-Za-z0-9]/gu, '').length || 1;
    const fit = footprint > 0 ? footprint / (chars * 0.52) : FONT_SIZE;
    const size = Math.min(FONT_SIZE, Math.max(fit, FONT_SIZE * 0.6));
    const t = document.createElementNS(ns, 'text');
    t.setAttribute('x', String(cx));
    t.setAttribute('y', String(cy));
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('dominant-baseline', 'central');
    t.setAttribute(LABEL_OVERLAY_ATTR, '');
    t.setAttribute('font-size', String(size));
    // LED-window legends read dark on the frosted glass (and silhouette when the
    // segment lights). Inline style beats the off-white [data-label-overlay] CSS.
    if (dark) t.style.setProperty('fill', '#16161a');
    if (clip) t.style.setProperty('clip-path', clip);
    t.textContent = text;
    svg.appendChild(t);
  };

  // Split a glyph set into baseline rows, each with its own centre and width —
  // so a multi-line label maps each line onto the exact row Sony drew it on.
  const rowsOf = (gs: readonly Glyph[]): { cx: number; cy: number; w: number }[] => {
    const sorted = [...gs].sort((a, b) => a.cy - b.cy);
    const rows: Glyph[][] = [];
    for (const g of sorted) {
      const last = rows[rows.length - 1];
      const anchor = last?.[0];
      if (last && anchor && Math.abs(anchor.cy - g.cy) <= 2) last.push(g);
      else rows.push([g]);
    }
    return rows.map((r) => {
      const x = Math.min(...r.map((g) => g.x));
      const x2 = Math.max(...r.map((g) => g.x + g.w));
      const y = Math.min(...r.map((g) => g.y));
      const y2 = Math.max(...r.map((g) => g.y + g.h));
      return { cx: (x + x2) / 2, cy: (y + y2) / 2, w: x2 - x };
    });
  };

  for (const c of controls) {
    // Unmatched controls keep Sony's own, correctly-placed vendor silkscreen —
    // never guess a position. (Guessing had put scene "2"/"5" onto the ND/CC
    // blocks once their on-button digits were excluded as control graphics.)
    if (c.own.length === 0) continue;
    const lines = c.label.split('\n');
    // Hide the matched outline glyphs and redraw line-by-line on the exact
    // silkscreen rows, each fitted to that row's original width.
    for (const b of c.own) for (const el of b.els) el.style.display = 'none';
    // STORE's vendor glyphs are drawn a row too low — down in the MASTER GAIN
    // band rather than against its own button. Draw it tight beneath the STORE
    // button instead, where it belongs.
    if (c.label === 'STORE') {
      const fw = Math.max(...c.own.map((b) => b.w));
      draw('STORE', c.box.x + c.box.w / 2, c.box.y + c.box.h + 3, fw);
      continue;
    }
    const rows = rowsOf(c.own.flatMap((b) => [...b.glyphs]));
    if (rows.length === lines.length) {
      rows.forEach((r, k) => {
        for (const g of glyphs) {
          if (Math.abs(g.cx - r.cx) <= r.w / 2 + 0.6 && Math.abs(g.cy - r.cy) <= FONT_SIZE * 0.7) {
            g.el.style.display = 'none';
          }
        }
        draw(lines[k] ?? '', r.cx, r.cy, r.w);
      });
    } else if (rows.length === 1 && rows[0]) {
      // Sony drew the label on ONE row though the catalogue splits it over two
      // (e.g. "MASTER\nBLACK", "MASTER\nGAIN"). Render it as a single joined
      // line on that row — laying the two catalogue lines into the one-row
      // height crushed them into an overlapping smear.
      const r = rows[0];
      draw(c.label.replace(/\n/gu, ' '), r.cx, r.cy, r.w);
    } else {
      // Line/row counts disagree: lay the lines across the block, fit to its width.
      const x = Math.min(...c.own.map((b) => b.x));
      const x2 = Math.max(...c.own.map((b) => b.x + b.w));
      const y = Math.min(...c.own.map((b) => b.y));
      const y2 = Math.max(...c.own.map((b) => b.y + b.h));
      const lh = (y2 - y) / lines.length;
      lines.forEach((ln, k) => {
        draw(ln, (x + x2) / 2, y + lh * (k + 0.5), x2 - x);
      });
    }
  }

  // Section headers are silkscreen text but not controls, so they carry no
  // catalogue entry. Convert the prominent ones to live type as well, matched
  // to a leftover (unclaimed) block by Sony's documented position and letter
  // count. Their decorative leader-lines are wide strokes, excluded from the
  // glyph set, so they remain — exactly as on the panel.
  // Section headers (SCENE FILE / WHITE / BLACK/FLARE) are NOT redrawn as live
  // type: they sit left-aligned in an asymmetric rule (short left bracket, long
  // right rule), and the centred live-type replacement left a gap that read
  // wrong. The operator confirmed (reference image) the vendor original is
  // correct, so Sony's own silkscreen glyphs are left in place — pixel-identical
  // to the panel. (AUTO SETUP stays live type via SILKSCREEN_LEGENDS; its rule is
  // symmetric so the live label fits.)
  const headers: { text: string; cx: number; cy: number }[] = [];
  const claimed = new Set<Block>(controls.flatMap((c) => c.own));
  for (const hd of headers) {
    const want = hd.text.replace(/[^A-Za-z0-9]/gu, '').length;
    let best: Block | null = null;
    let bestScore = Infinity;
    for (const b of blocks) {
      if (claimed.has(b)) continue;
      const dist = Math.hypot(b.x + b.w / 2 - hd.cx, b.y + b.h / 2 - hd.cy);
      if (dist > 12 || b.els.length < want - 3 || b.els.length > want + 6) continue;
      const score = dist + Math.abs(b.els.length - want);
      if (score < bestScore) {
        bestScore = score;
        best = b;
      }
    }
    if (!best) continue;
    claimed.add(best);
    for (const el of best.els) el.style.display = 'none';
    draw(hd.text, best.x + best.w / 2, best.y + best.h / 2, best.w);
  }

  // A clip = the panel MINUS the iris lever disc, in user space. Live legends
  // flagged `clipLever` are painted with it so the rows the proud lever covers
  // never bleed through it — and stay clipped even while the cap is dragged.
  let leverClipUrl = '';
  {
    const leverEl =
      svg.querySelector<SVGGraphicsElement>('[data-prim-id="p0636-path"]') ??
      svg.querySelector<SVGGraphicsElement>('[data-component-id="cmp-iris-lever"]');
    if (leverEl) {
      const lb = userBox(leverEl);
      const cx = lb.x + lb.w / 2;
      const cy = lb.y + lb.h / 2;
      const r = Math.max(lb.w, lb.h) / 2;
      const cpNs = 'http://www.w3.org/2000/svg';
      const cp = document.createElementNS(cpNs, 'clipPath');
      cp.setAttribute('id', 'iris-lever-clip');
      cp.setAttribute('clipPathUnits', 'userSpaceOnUse');
      const p = document.createElementNS(cpNs, 'path');
      // Big rect (whole panel) with the lever circle punched out via even-odd.
      const ring = `M ${String(cx - r)} ${String(cy)} a ${String(r)} ${String(r)} 0 1 0 ${String(2 * r)} 0 a ${String(r)} ${String(r)} 0 1 0 ${String(-2 * r)} 0 Z`;
      p.setAttribute('d', `M -10000 -10000 H 10000 V 10000 H -10000 Z ${ring}`);
      p.setAttribute('clip-rule', 'evenodd');
      cp.appendChild(p);
      svg.appendChild(cp);
      leverClipUrl = 'url(#iris-lever-clip)';
    }
  }

  // Legend pass — convert every silkscreen string the matcher/header passes left
  // behind (SILKSCREEN_LEGENDS) to live type at its exact vendor position, then
  // hide the vendor glyphs. Anchored by prim-id, so placement is measured not
  // guessed; coverage is complete by construction. Runs BEFORE forceShow, and
  // none of these ids are in forceShow, so the hides stick.
  for (const entry of SILKSCREEN_LEGENDS) {
    const items = entry.anchorIds
      .map((id) => svg.querySelector<SVGGraphicsElement>(`[data-prim-id="${id}"]`))
      .filter((el): el is SVGGraphicsElement => el !== null)
      .map((el) => ({ el, b: userBox(el) }));
    if (items.length === 0) continue;
    if (entry.hideOnly) {
      for (const it of items) it.el.style.display = 'none';
      continue;
    }
    const drawBox = (its: typeof items, text: string): void => {
      const x = Math.min(...its.map((i) => i.b.x));
      const x2 = Math.max(...its.map((i) => i.b.x + i.b.w));
      const y = Math.min(...its.map((i) => i.b.y));
      const y2 = Math.max(...its.map((i) => i.b.y + i.b.h));
      for (const i of its) i.el.style.display = 'none';
      draw(text, (x + x2) / 2, (y + y2) / 2, x2 - x, entry.dark, entry.clipLever ? leverClipUrl : '');
    };
    if (entry.rows) {
      // Group anchor glyphs into baseline rows (top→bottom), one string per row.
      const sorted = [...items].sort((a, b) => a.b.y - b.b.y);
      const rows: (typeof items)[] = [];
      for (const it of sorted) {
        const cy = it.b.y + it.b.h / 2;
        const last = rows[rows.length - 1];
        const anchorCy = last?.[0] ? last[0].b.y + last[0].b.h / 2 : null;
        if (last && anchorCy !== null && Math.abs(anchorCy - cy) <= 2) last.push(it);
        else rows.push([it]);
      }
      rows.forEach((row, k) => {
        const text = entry.rows?.[k];
        if (!text) return; // no string for this row → leave vendor glyph (the sweep flags it)
        drawBox(row, text);
      });
    } else {
      drawBox(items, entry.text ?? '');
    }
  }

  // Restore any silkscreen mark the cluster matcher wrongly swallowed — the left
  // half of the WHITE underline bracket, and the coloured button bars. Runs last
  // so it overrides the glyph-hiding above.
  const forceShow = [
    ...KEPT_PRIMITIVE_IDS,
    ...SILK_COLOUR_MARKS.map((m) => m.id),
    ...SOLID_OFFWHITE_IDS,
    ...DARK_LED_TEXT_IDS,
    ...MILKY_KEY_IDS,
  ];
  for (const id of forceShow) {
    const el = svg.querySelector<SVGElement>(`[data-prim-id="${id}"]`);
    if (el) el.style.display = '';
  }
}

/**
 * Crop the SVG view to the FULL RCP-3500 panel — the 102 × 355 mm
 * chassis including the top and bottom mounting flanges (the ears with
 * the 2 × ø5 holes), per the Operating Instructions Specifications
 * (p. 57): width 102 mm, full height 355 mm, active face-plate 310 mm.
 *
 * The substrate is Sony's own operating-instructions vector
 * (page-09-overview.svg), RCP-3500 isolated from the RCP-3501 twin and
 * the callout leader-lines by geometric pruning. The full panel occupies
 * this rect (measured by getBBox over the kept geometry):
 *
 *   x = 111.85, y = 164.07, w = 115.69, h = 402.17  (SVG units)
 *
 * That is 115.69 : 402.17 = 1 : 3.476 ≈ the spec's 102 : 355 = 1 : 3.48
 * (the page-9 drawing is to scale for the full panel). The active face
 * is the inner 310 mm; the flanges add ~22.5 mm at each end. The
 * canonical-space affine (index.ts) is isotropic over this rect:
 * scale 8.8166 → canonical 1020 × 3546. Isotropic keeps the round
 * knobs round and the HTML overlays coincident with the drawn windows.
 */
export const CHASSIS_VIEWBOX = {
  x: 111.85,
  y: 164.07,
  width: 115.69,
  height: 402.17,
} as const;
